/*
 * Назначение: журнал попыток входа на странице «Безопасность».
 * Функционал: таблица «когда, кто, откуда, чем закончилось» за выбранный
 * период. По ней видно, ломают ли панель и с каких адресов, без чтения логов
 * сервера.
 */

import type { LoginAttempt } from "../../../lib/security-api";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

/** Человеческое объяснение причины отказа. */
const REASONS: Record<string, string> = {
  bad_credentials: "неверный пароль",
  rate_limited: "заблокировано, слишком много попыток",
  inactive: "пользователь отключён",
  self_change_pwd_bad_current: "неверный текущий пароль при смене",
  twofa: "подтверждено кодом",
};

function reasonLabel(attempt: LoginAttempt): string {
  if (attempt.success) return REASONS[attempt.reason ?? ""] ?? "вход выполнен";
  return REASONS[attempt.reason ?? ""] ?? attempt.reason ?? "отказ";
}

export function LoginAttemptsCard({
  attempts,
  hours,
  onHoursChange,
}: {
  attempts: LoginAttempt[];
  hours: number;
  onHoursChange: (hours: number) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Журнал входов</CardTitle>
        <CardDescription>
          Все попытки входа: удачные и нет. Если видишь серию отказов с
          незнакомого адреса — панель подбирают, и защита уже держит паузу.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          {[24, 48, 168].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => onHoursChange(value)}
              className={`rounded-md border px-3 py-1 text-xs ${
                hours === value ? "border-primary text-primary" : "border-border"
              }`}
            >
              {value === 168 ? "неделя" : `${value} ч`}
            </button>
          ))}
        </div>

        {attempts.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            За выбранный период попыток входа не было.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr className="text-left">
                  <th className="py-2 pr-3">Когда</th>
                  <th className="py-2 pr-3">Логин</th>
                  <th className="py-2 pr-3">Адрес</th>
                  <th className="py-2 pr-3">Итог</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((attempt, index) => (
                  <tr key={`${attempt.created_at}-${index}`} className="border-t border-border">
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {new Date(attempt.created_at).toLocaleString("ru-RU")}
                    </td>
                    <td className="py-2 pr-3 font-mono">{attempt.login}</td>
                    <td className="py-2 pr-3 font-mono">{attempt.ip ?? "—"}</td>
                    <td className="py-2 pr-3">
                      <Badge variant={attempt.success ? "outline" : "secondary"}>
                        {reasonLabel(attempt)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
